import { Link } from 'gatsby';
import React, { useMemo, useState } from 'react';
import Footer from './Footer';
import Header from './Header';
import Layout from './Layout';
import {
  byPublishedAtDesc,
  formatPublishedAt,
  isReleased,
  limitExcerpt,
} from '../utils/blog';

const matchesQuery = (post, query) => {
  if (!query) return true;
  const haystack = [post.title, post.excerpt]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
};

const BlogPage = ({ pageContext }) => {
  const posts = byPublishedAtDesc((pageContext.posts || []).filter(isReleased));
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();
  const visiblePosts = useMemo(
    () => posts.filter((post) => matchesQuery(post, normalizedQuery)),
    [posts, normalizedQuery]
  );
  const isSearching = normalizedQuery.length > 0;

  return (
    <Layout titlePrefixes={['Blog']}>
      <Header solid />
      <style>{`
        .blog-list-page {
          background: #e8eded;
          padding: 110px 40px 120px;
          min-height: 100vh;
        }
        .blog-list-wrap {
          max-width: 1040px;
          margin: 0 auto;
        }
        .blog-list-heading {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 1.5rem;
          margin: 0 0 1.5rem;
        }
        .blog-list-heading h1 {
          font-size: 2.15rem;
          line-height: 1.25;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 0.35rem;
        }
        .blog-list-heading p {
          margin: 0;
          color: #6c757d;
        }
        .blog-list-search {
          position: relative;
          flex: 0 1 320px;
        }
        .blog-list-search i {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #8a9292;
          font-size: 0.9rem;
          pointer-events: none;
        }
        .blog-list-search input {
          width: 100%;
          border: 1px solid #dce3e3;
          border-radius: 10px;
          background: #fff;
          padding: 0.7rem 1rem 0.7rem 2.4rem;
          font-size: 1rem;
          color: #1a1a1a;
          box-shadow: 0 10px 40px rgba(26, 26, 26, 0.06);
        }
        .blog-list-search input:focus {
          outline: none;
          border-color: #009e9a;
        }
        .blog-list-items {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .blog-list-empty,
        .blog-list-item {
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(26, 26, 26, 0.08);
          border: 1px solid #dce3e3;
        }
        .blog-list-empty {
          margin: 0;
          padding: 2.5rem 2.75rem;
          color: #6c757d;
        }
        .blog-list-item {
          display: flex;
          gap: 0;
          align-items: stretch;
          margin: 0;
          padding: 0;
        }
        .blog-list-item-featured {
          flex-direction: column;
        }
        .blog-list-image {
          display: block;
          flex: 0 0 260px;
          width: 260px;
          align-self: stretch;
          overflow: hidden;
          background: #dfe6e6;
        }
        .blog-list-item-featured .blog-list-image {
          flex: none;
          width: 100%;
          height: 260px;
        }
        .blog-list-body {
          flex: 1;
          min-width: 0;
          padding: 1.5rem 1.75rem;
        }
        .blog-list-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .blog-list-body h3 {
          margin: 0 0 0.5rem;
          font-size: 1.35rem;
        }
        .blog-list-item-featured .blog-list-body {
          padding: 1.75rem 2.75rem 2rem;
        }
        .blog-list-item-featured .blog-list-body h3 {
          font-size: 1.7rem;
        }
        .blog-list-body h3 a {
          color: #222;
          text-decoration: none;
        }
        .blog-list-body h3 a:hover {
          color: #009e9a;
        }
        .blog-list-date {
          font-size: 0.9rem;
          margin-bottom: 0.75rem;
        }
        .blog-list-excerpt {
          margin-bottom: 0.75rem;
        }
        .blog-list-read-more {
          color: #009e9a;
          font-weight: 600;
        }
        @media (max-width: 767px) {
          .blog-list-page {
            padding: 96px 12px 48px;
          }
          .blog-list-heading {
            flex-direction: column;
            align-items: stretch;
          }
          .blog-list-search {
            flex: none;
            width: 100%;
          }
          .blog-list-item {
            flex-direction: column;
            gap: 0;
          }
          .blog-list-image {
            flex: none;
            width: 100%;
            aspect-ratio: 16 / 9;
          }
          .blog-list-item-featured .blog-list-image {
            aspect-ratio: auto;
            height: 180px;
          }
          .blog-list-body,
          .blog-list-item-featured .blog-list-body,
          .blog-list-empty {
            padding: 1.25rem;
          }
          .blog-list-heading h1 {
            font-size: 1.7rem;
          }
        }
      `}</style>

      <section className="blog-list-page">
        <div className="blog-list-wrap">
          <header className="blog-list-heading">
            <div>
              <h1>Blog</h1>
              <p>News, product updates, and stories from ErgZone.</p>
            </div>
            <label className="blog-list-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search posts"
                aria-label="Search posts"
              />
            </label>
          </header>

          <div className="blog-list-items">
            {posts.length === 0 ? (
              <p className="blog-list-empty">No posts yet. Check back soon.</p>
            ) : visiblePosts.length === 0 ? (
              <p className="blog-list-empty">No posts match your search.</p>
            ) : (
              visiblePosts.map((post, index) => {
                const featured = !isSearching && index === 0;
                return (
                  <article
                    key={post.id}
                    className={
                      featured
                        ? 'blog-list-item blog-list-item-featured'
                        : 'blog-list-item'
                    }
                  >
                    {post.imageUrl && (
                      <Link
                        to={`/blog/${post.slug}`}
                        className="blog-list-image"
                      >
                        <img src={post.imageUrl} alt="" />
                      </Link>
                    )}
                    <div className="blog-list-body">
                      <h3>
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h3>
                      {post.publishedAt && (
                        <p className="text-muted blog-list-date">
                          {formatPublishedAt(post.publishedAt)}
                        </p>
                      )}
                      {post.excerpt && (
                        <p className="blog-list-excerpt">
                          {limitExcerpt(post.excerpt)}
                        </p>
                      )}
                      <Link
                        to={`/blog/${post.slug}`}
                        className="blog-list-read-more"
                      >
                        Read more
                      </Link>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </div>
      </section>

      <Footer />
    </Layout>
  );
};

export default BlogPage;
